import * as React from "react"
import { Slot, Slottable } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { LoadingIndicator } from "./pixel-grid"
import { WorkspaceActionFormContext } from "./workspace-action-context"

const buttonVariants = cva(
  "unitfield-control unitfield-touch-target relative cursor-pointer inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium leading-none disabled:pointer-events-none disabled:opacity-50 data-[pending=true]:disabled:opacity-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:relative [&_svg]:top-[-0.5px] [&_svg]:!m-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-focus-gap aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary-600 dark:bg-primary-600 dark:text-white dark:hover:bg-primary-500",
        destructive:
          "bg-status-danger-1 text-status-danger-0 shadow-xs hover:bg-status-danger-1/90 focus-visible:ring-status-danger-1",
        destructiveOutline: "ring-2 ring-status-danger-1 bg-background text-status-danger-1 shadow-xs hover:bg-status-danger-0 hover:text-status-danger-1",
        outline:
          "border border-border/70 bg-card shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs inset-ring inset-ring-border/70 hover:bg-accent hover:text-accent-foreground",
        ghost:
          "hover:bg-secondary hover:text-secondary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        transparent: "bg-transparent",
        input: "bg-input text-foreground hover:bg-input/80",
        muted: "bg-muted text-foreground hover:bg-muted/80",
        mutedBordered: "ring-2 ring-input bg-muted shadow-xs hover:bg-muted-foreground/10 hover:text-foreground",
        filterControl: "border-0 ring-inset ring-0 bg-surface-control shadow-none hover:bg-surface-control focus-visible:ring-offset-0",
        tableAction:
          "border border-border/70 bg-card text-foreground shadow-xs hover:border-border hover:bg-accent focus-visible:ring-ring",
        tableActionDestructive:
          "border border-status-danger-1/25 bg-status-danger-0 text-status-danger-1 shadow-xs hover:border-status-danger-1/40 hover:bg-status-danger-0/80 focus-visible:ring-status-danger-1",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3 [--button-pending-mark:18px]",
        // md: Input size="md" ve Select default ile eşleşir (her ikisi de h-9)
        md: "h-9 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 [--button-pending-mark:16px]",
        sm: "h-9 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 [--button-pending-mark:16px]",
        xs: "h-8 rounded-sm gap-1 px-2.5 text-xs has-[>svg]:px-2 [--button-pending-mark:14px] [--button-pending-gap:5px]",
        lg: "h-11 rounded-md gap-2 px-6 has-[>svg]:px-4 [--button-pending-mark:20px] [--button-pending-gap:8px]",
        icon: "size-10 max-sm:size-9 [--button-pending-mark:18px]",
        iconMd: "size-8 [--button-pending-mark:16px]",
        iconSm: "size-6 [--button-pending-mark:13px]",
      },
      shape: {
        default: "",
        pill: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  }
)

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Shows the compact action indicator while the operation is pending. */
    pending?: boolean
    /** Optional visible and accessible label for the pending state. */
    pendingLabel?: React.ReactNode
    /** Accessible name used while pending when pendingLabel is not a string. */
    pendingAriaLabel?: string
    /** Optional decorative replacement for the compact action indicator. */
    pendingIndicator?: React.ReactNode
    /** Keep a pending control enabled when it is safe to cancel the action. */
    pendingCancelable?: boolean
  }

function blockPendingClick(event: React.MouseEvent<HTMLElement>) {
  event.preventDefault()
  event.stopPropagation()
}

function blockPendingKeyPress(event: React.KeyboardEvent<HTMLElement>) {
  event.preventDefault()
  event.stopPropagation()
}

function blockPendingKeyboard(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
    event.preventDefault()
    event.stopPropagation()
  }
}

const pendingAsChildInvariant =
  "Button pending asChild requires a native <button> or an <a> with a non-empty href."

function requirePendingAsChild(children: React.ReactNode) {
  type PendingAsChildElement = React.HTMLAttributes<HTMLElement> & { href?: string }

  if (
    React.isValidElement<PendingAsChildElement>(children) &&
    (children.type === "button" ||
      (children.type === "a" &&
        typeof children.props.href === "string" &&
        children.props.href.trim().length > 0))
  ) {
    return children
  }

  throw new Error(pendingAsChildInvariant)
}

function Button({
  className,
  variant,
  size,
  shape,
  asChild = false,
  pending = false,
  pendingLabel,
  pendingAriaLabel,
  pendingIndicator,
  pendingCancelable = false,
  disabled,
  children,
  onClick,
  onClickCapture,
  onAuxClick,
  onAuxClickCapture,
  onKeyDown,
  onKeyDownCapture,
  onKeyUp,
  onKeyUpCapture,
  onKeyPress,
  onKeyPressCapture,
  "aria-busy": ariaBusy,
  "aria-label": ariaLabel,
  "aria-disabled": ariaDisabled,
  ...props
}: ButtonProps) {
  const actionForm = React.useContext(WorkspaceActionFormContext)
  const nativeForm = !asChild && (props.type === "submit" || props.type === "reset")
    ? props.form ?? actionForm
    : props.form
  const Comp = asChild ? Slot : "button"
  const resolvedDisabled = asChild
    ? disabled
    : pending && !pendingCancelable
      ? true
      : disabled
  const resolvedAriaLabel = pending
    ? pendingAriaLabel ??
      (typeof pendingLabel === "string" && pendingLabel.trim() ? pendingLabel : ariaLabel)
    : ariaLabel
  const resolvedAriaDisabled = pending && !pendingCancelable ? true : ariaDisabled
  // The mark follows the size token (`--button-pending-mark`), so it keeps the
  // same weight next to the label from 24px icon buttons to 44px sheets.
  const indicator = pendingIndicator ?? (
    <LoadingIndicator
      preset="compact"
      decorative
      style={{ "--unitfield-loading-size": "var(--button-pending-mark, 18px)" } as React.CSSProperties}
    />
  )
  const renderContent = (contentChildren: React.ReactNode) => (
    <span className="inline-grid min-w-0 items-center justify-center">
      <span
        className={cn(
          "col-start-1 row-start-1 inline-flex min-w-0 items-center gap-1.5",
          pending && "opacity-0",
        )}
      >
        {contentChildren}
      </span>
      {pending ? (
        <span
          aria-hidden="true"
          data-slot="button-pending-content"
          className="pointer-events-none col-start-1 row-start-1 inline-flex items-center justify-center gap-[var(--button-pending-gap,6px)]"
        >
          <span
            data-slot="button-pending-indicator-slot"
            className="inline-flex size-[var(--button-pending-mark,18px)] shrink-0 items-center justify-center"
          >
            {indicator}
          </span>
          {pendingLabel !== undefined && pendingLabel !== null ? (
            <span>{pendingLabel}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  )

  if (!pending) {
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, shape, className }))}
        disabled={disabled}
        aria-busy={ariaBusy}
        aria-label={ariaLabel}
        aria-disabled={ariaDisabled}
        onClick={onClick}
        onClickCapture={onClickCapture}
        onAuxClick={onAuxClick}
        onAuxClickCapture={onAuxClickCapture}
        onKeyDown={onKeyDown}
        onKeyDownCapture={onKeyDownCapture}
        onKeyUp={onKeyUp}
        onKeyUpCapture={onKeyUpCapture}
        onKeyPress={onKeyPress}
        onKeyPressCapture={onKeyPressCapture}
        {...props}
        form={nativeForm}
        data-variant={variant ?? "default"}
      >
        {children}
      </Comp>
    )
  }

  const pendingChild = asChild ? requirePendingAsChild(children) : null
  const pendingChildProps: React.HTMLAttributes<HTMLElement> & { disabled?: boolean } = {
    "aria-busy": true,
  }

  if (resolvedAriaDisabled !== undefined) {
    pendingChildProps["aria-disabled"] = resolvedAriaDisabled
  }
  if (resolvedAriaLabel !== undefined) {
    pendingChildProps["aria-label"] = resolvedAriaLabel
  }
  if (asChild && !pendingCancelable) {
    pendingChildProps.onClick = blockPendingClick
    pendingChildProps.onClickCapture = blockPendingClick
    pendingChildProps.onAuxClick = blockPendingClick
    pendingChildProps.onAuxClickCapture = blockPendingClick
    pendingChildProps.onKeyDown = blockPendingKeyboard
    pendingChildProps.onKeyDownCapture = blockPendingKeyboard
    pendingChildProps.onKeyUp = blockPendingKeyboard
    pendingChildProps.onKeyUpCapture = blockPendingKeyboard
    pendingChildProps.onKeyPress = blockPendingKeyPress
    pendingChildProps.onKeyPressCapture = blockPendingKeyPress
    if (
      pendingChild &&
      typeof pendingChild.type === "string" &&
      ["button", "input", "select", "textarea"].includes(pendingChild.type)
    ) {
      pendingChildProps.disabled = true
    }
  }

  const guardedPendingChild = pendingChild
    ? React.cloneElement(pendingChild, pendingChildProps)
    : null

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, shape, className }))}
      disabled={resolvedDisabled}
      aria-busy={pending ? true : ariaBusy}
      aria-label={resolvedAriaLabel}
      aria-disabled={resolvedAriaDisabled}
      onClick={asChild && !pendingCancelable ? undefined : onClick}
      onClickCapture={asChild && !pendingCancelable ? undefined : onClickCapture}
      onAuxClick={asChild && !pendingCancelable ? undefined : onAuxClick}
      onAuxClickCapture={asChild && !pendingCancelable ? undefined : onAuxClickCapture}
      onKeyDown={asChild && !pendingCancelable ? undefined : onKeyDown}
      onKeyDownCapture={asChild && !pendingCancelable ? undefined : onKeyDownCapture}
      onKeyUp={asChild && !pendingCancelable ? undefined : onKeyUp}
      onKeyUpCapture={asChild && !pendingCancelable ? undefined : onKeyUpCapture}
      onKeyPress={asChild && !pendingCancelable ? undefined : onKeyPress}
      onKeyPressCapture={asChild && !pendingCancelable ? undefined : onKeyPressCapture}
      {...props}
      form={nativeForm}
      data-variant={variant ?? "default"}
      data-pending="true"
    >
      {asChild ? (
        <Slottable child={guardedPendingChild}>
          {(slottedChildren) => renderContent(slottedChildren)}
        </Slottable>
      ) : (
        renderContent(children)
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
